import { groupBy, isNil, sortBy, sumBy } from 'lodash';
import type { Portfolio } from 'venn-api';
import { logMessageToSentry } from '../error-logging';
import { assertNotNil } from '../utils/assert';
import { isCashNode, isFundNode, isStrategyNode, partitionNodes } from './portfolioUtils';

export const mapPortfolioToNodes = (portfolio?: Portfolio): Map<number, Portfolio> => {
  const mapped = new Map<number, Portfolio>();
  const mapRecursive = (node?: Portfolio) => {
    if (!node) {
      return;
    }
    mapped.set(node.id, node);
    if (!node.children) {
      return;
    }
    node.children.forEach((child: Portfolio) => {
      mapRecursive(child);
    });
  };
  mapRecursive(portfolio);
  return mapped;
};

/**
 * Transforms a `Portfolio` into a "normalized" portfolio, meaning that we can quarantee that each strategy has no more
 * than 1 occurrence of a `Fund` with a specific `fund.id`.
 */
export const normalizePortfolio = (portfolio: Portfolio): Portfolio => {
  if (portfolio.fund || !portfolio.children || portfolio.children.length === 0 || portfolio.designation === 'CASH') {
    return { ...portfolio };
  }

  const childFunds = [...portfolio.children].filter(isFundNode);
  const sortedChildFunds = sortBy(childFunds, ['fund.id']);

  // Map from `strategy.fund.id` to `strategy.allocation` for FUNDS.
  // It will be used to sum up the allocations of repeated funds under the same strategy.
  const childFundToAllocation = new Map<string, number | undefined>();
  sortedChildFunds.forEach((childFund: Portfolio) => {
    if (!childFund.fund) {
      return;
    }
    if (childFundToAllocation.has(childFund.fund.id)) {
      // If this fund is already a part of this strategy, add the allocations together.
      let partialChildFundAllocation = childFundToAllocation.get(childFund.fund.id) ?? 0;
      partialChildFundAllocation += childFund.allocation ?? 0;
      childFundToAllocation.set(childFund.fund.id, partialChildFundAllocation);
      return;
    }
    childFundToAllocation.set(childFund.fund.id, childFund.allocation);
  });

  const normalizedPortfolio = {
    ...portfolio,
    children: [] as Portfolio[],
  };
  portfolio.children.forEach((child: Portfolio) => {
    // For children that are STRATEGIES or CASH BALANCES, normalize them first and then add them as children
    if (isStrategyNode(child) || isCashNode(child)) {
      const normalizedChild = normalizePortfolio(child);
      if (normalizedChild === undefined) {
        return;
      }
      normalizedPortfolio.children.push({ ...normalizedChild });
      return;
    }
    // Only add child fund if it's present in `childFundToAllocation` map. For funds that were doubled/tripled etc,
    // they will get deleted after their first read, so they won't appear twice in strategy's children.
    const fund = assertNotNil(child.fund);
    if (childFundToAllocation.has(fund.id)) {
      normalizedPortfolio.children.push({
        ...child,
        allocation: childFundToAllocation.get(fund.id),
        netAssets: childFundToAllocation.get(fund.id),
        cashBalance: 0,
      });
      // Delete the child after it's been used.
      childFundToAllocation.delete(fund.id);
    }
  });
  return normalizedPortfolio;
};

export const calculateComparison = (
  // `portfolio` has to be NORMALIZED, see: `normalizePortfolio(portfolio: Portfolio);`
  portfolio?: Portfolio,
  // `comparePortfolio` has to be NORMALIZED, see: `normalizePortfolio(portfolio: Portfolio);`
  comparePortfolio?: Portfolio,
): [Map<number, Portfolio>, Map<number, Portfolio[]>] => {
  if (!portfolio || !comparePortfolio) {
    return [new Map(), new Map()];
  }

  const allCompareNodes = new Map<number, Portfolio>();
  const allGhostChildren = new Map<number, Portfolio[]>();
  mapPortfolioToCompare(portfolio, comparePortfolio, allCompareNodes, allGhostChildren);

  return [allCompareNodes, allGhostChildren];
};

function mapPortfolioToCompare(
  // `portfolio` has to be NORMALIZED, see: `normalizePortfolio(portfolio: Portfolio);`
  portfolio: Portfolio,
  // `comparePortfolio` has to be NORMALIZED, see: `normalizePortfolio(portfolio: Portfolio);`
  comparePortfolio: Portfolio | undefined,
  allCompareNodes: Map<number, Portfolio>,
  allGhostChildren: Map<number, Portfolio[]>,
) {
  if (!comparePortfolio || !portfolio) {
    return;
  }

  allCompareNodes.set(portfolio.id, comparePortfolio);

  if (!isStrategyNode(portfolio)) {
    return;
  }

  const {
    funds: compareChildFunds,
    strategies: compareChildStrategies,
    cashPositions: compareChildCashPositions,
  } = partitionNodes(comparePortfolio.children);

  // (1-funds): For each fund child of `comparePortfolio` node, add an entry to `compareChildFundIdToNode`
  //   that retrieves the node for the fund id.

  // Map from fund id to Portfolio node, for each fund that's a child of this compare node.
  const compareChildFundIdToNode = new Map<string, Portfolio>();
  compareChildFunds.forEach((compareChildFund: Portfolio) => {
    if (!compareChildFund.fund) {
      logMessageToSentry('Fund portfolio should have fund child');
      return;
    }
    if (compareChildFundIdToNode.has(compareChildFund.fund.id)) {
      // If compare node has more than one occurrence of this fund, it means that the `comparePortfolio` is not
      // normalized and this method can't return deterministic results.
      throw new Error(
        `\`comparePortfolio\` passed to \`mapPortfolioToCompare\` is not normalized: ${portfolio.name} has two child funds \`${compareChildFund.name}\` (id: ${compareChildFund.fund.id}). Normalize your portfolio using \`normalizePortfolio(portfolio: Portfolio);\` before calling this function.`,
      );
    }
    compareChildFundIdToNode.set(compareChildFund.fund.id, compareChildFund);
  });

  // (1-strategies): For each strategy child of `comparePortfolio`, add an entry to the end of the array in
  //   `compareChildStrategyNameToNodes`. Since we can't enforce different strategy names for all children, we're going
  //   to match them using our "best guess": the original order. The "best guess" can be improved later if we want to.

  // Map from strategy name to Portfolio nodes, for each strategy that's a child of this compare node. Strategies of the
  // same name go together on the list under the same name key.
  const compareChildStrategyNameToNodes = new Map<string, Portfolio[]>();
  compareChildStrategies.forEach((compareChildStrategy: Portfolio) => {
    if (compareChildStrategyNameToNodes.has(compareChildStrategy.name)) {
      const nodes = compareChildStrategyNameToNodes.get(compareChildStrategy.name);

      if (nodes === undefined) {
        logMessageToSentry("Can't find compare child");
        return;
      }
      nodes.push(compareChildStrategy);
      compareChildStrategyNameToNodes.set(compareChildStrategy.name, nodes);
      return;
    }
    compareChildStrategyNameToNodes.set(compareChildStrategy.name, [compareChildStrategy]);
  });

  // (1-cash positions): Do the same as with strategies
  const compareChildCashNameToNodes = new Map<string, Portfolio[]>(
    Object.entries(groupBy(compareChildCashPositions, 'name')),
  );

  // (2-all three types): Go over children of `portfolio`. For each child, check if it has a corresponding node in
  //   `comparePortfolio` using the right map (for funds/strategies/cash). Delete the node from the map after "using" it.
  (portfolio.children || []).forEach((child: Portfolio) => {
    if (isFundNode(child)) {
      const fund = assertNotNil(child.fund);
      if (compareChildFundIdToNode.has(fund.id)) {
        mapPortfolioToCompare(child, compareChildFundIdToNode.get(fund.id), allCompareNodes, allGhostChildren);
        compareChildFundIdToNode.delete(fund.id);
      }
      return;
    }
    if (isCashNode(child)) {
      const nodes = compareChildCashNameToNodes.get(child.name);
      if (!isNil(nodes)) {
        const [usedNode, ...restNodes] = nodes;
        mapPortfolioToCompare(child, usedNode, allCompareNodes, allGhostChildren);
        if (restNodes.length > 0) {
          compareChildCashNameToNodes.set(child.name, restNodes);
        } else {
          compareChildCashNameToNodes.delete(child.name);
        }
      }
      return;
    }
    // Child strategies need a recursive call if they have a corresponding node
    if (compareChildStrategyNameToNodes.has(child.name)) {
      const nodes = [...compareChildStrategyNameToNodes.get(child.name)!];
      if (nodes.length === 0) {
        // This shouldn't happen; we should remove empty arrays. But just to be safe.
        compareChildStrategyNameToNodes.delete(child.name);
        return;
      }
      const [usedNode, ...restNodes] = nodes;
      mapPortfolioToCompare(child, usedNode, allCompareNodes, allGhostChildren);
      if (restNodes.length > 0) {
        compareChildStrategyNameToNodes.set(child.name, restNodes);
      } else {
        compareChildStrategyNameToNodes.delete(child.name);
      }
    }
  });

  // (3): Go over leftover children of `comparePortfolio` in all maps. These will become "ghost children" of the
  //   current strategy.
  let leftoverCompareChildren: Portfolio[] = [];
  for (const node of compareChildFundIdToNode.values()) {
    leftoverCompareChildren.push(node);
  }
  for (const nodes of compareChildStrategyNameToNodes.values()) {
    leftoverCompareChildren = [...leftoverCompareChildren, ...nodes];
  }
  for (const nodes of compareChildCashNameToNodes.values()) {
    leftoverCompareChildren = [...leftoverCompareChildren, ...nodes];
  }
  compareChildFundIdToNode.clear();
  compareChildStrategyNameToNodes.clear();
  compareChildCashNameToNodes.clear();
  if (leftoverCompareChildren.length > 0) {
    allGhostChildren.set(portfolio.id, leftoverCompareChildren);
  }
}

export const deleteConstrainingFundFromPortfolio = (portfolio: Portfolio, fundId: string): Portfolio => {
  if (!portfolio?.children?.length) {
    return portfolio;
  }
  const children = portfolio.children
    .filter((strategy) => !strategy.fund || strategy.fund.id !== fundId)
    .map((strategy) => deleteConstrainingFundFromPortfolio(strategy, fundId));

  return {
    ...portfolio,
    children,
    allocation: sumBy(children, 'allocation'),
    cashBalance: sumBy(children, 'cashBalance'),
    netAssets: sumBy(children, 'netAssets'),
  };
};
